"use client";

type FunnelEventName =
  | "funnel_product_click"
  | "funnel_add_to_cart"
  | "funnel_cart_open"
  | "funnel_checkout_start"
  | "funnel_checkout_step_progress"
  | "funnel_checkout_validation_failure"
  | "funnel_order_success"
  | "funnel_order_failure"
  | "funnel_recommendation_module_view"
  | "funnel_recommendation_feedback"
  | "funnel_post_purchase_upsell_view"
  | "funnel_post_purchase_upsell_cta"
  | "funnel_post_purchase_upsell_product_click"
  | "funnel_post_purchase_addon_completed"
  | "funnel_ux_initiative_exposed";

type FunnelEventPayload = {
  event: FunnelEventName;
  product_id?: string;
  cart_value_bucket?: string;
  step?: 1 | 2 | 3;
  error_type?: "validation" | "payment" | "order";
  payment_method?: string;
  fulfillment_mode?: string;
  order_id?: string;
  source?: string;
  /** Issue #71, filter in BI for UX revenue attribution */
  ux_initiative_id?: string;
  /** Surface key for exposure events (e.g. cart_drawer_footer) */
  ux_surface?: string;
  [key: string]: unknown;
};

const DEDUPE_PREFIX = "dank:funnel:dedupe:";
const SESSION_ID_KEY = "dank:funnel:session-id";

const memoryDedupe = new Map<string, number>();

function nowMs() {
  return Date.now();
}

function readSessionStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorage(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage errors in private mode or disabled storage.
  }
}

function isDuplicateKey(key: string, ttlMs: number): boolean {
  const current = nowMs();
  const memoryTs = memoryDedupe.get(key);
  if (typeof memoryTs === "number" && current - memoryTs < ttlMs) {
    return true;
  }

  const persisted = readSessionStorage(`${DEDUPE_PREFIX}${key}`);
  if (persisted) {
    const persistedTs = Number(persisted);
    if (Number.isFinite(persistedTs) && current - persistedTs < ttlMs) {
      memoryDedupe.set(key, persistedTs);
      return true;
    }
  }

  memoryDedupe.set(key, current);
  writeSessionStorage(`${DEDUPE_PREFIX}${key}`, String(current));
  return false;
}

export function getCartValueBucket(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";
  if (value < 50) return "1-49";
  if (value < 100) return "50-99";
  if (value < 200) return "100-199";
  if (value < 300) return "200-299";
  return "300+";
}

export function categorizeCheckoutError(rawMessage: string): "validation" | "payment" | "order" {
  const message = String(rawMessage || "").toLowerCase();
  if (
    message.includes("card") ||
    message.includes("payment") ||
    message.includes("preauth") ||
    message.includes("cvv") ||
    message.includes("expiry")
  ) {
    return "payment";
  }
  if (
    message.includes("required") ||
    message.includes("invalid") ||
    message.includes("fill in") ||
    message.includes("must be") ||
    message.includes("empty")
  ) {
    return "validation";
  }
  return "order";
}

function getSessionId(): string {
  const existing = readSessionStorage(SESSION_ID_KEY);
  if (existing) return existing;
  const newId = `${nowMs().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  writeSessionStorage(SESSION_ID_KEY, newId);
  return newId;
}

export function trackFunnelEvent(payload: FunnelEventPayload): void {
  if (typeof window === "undefined") return;

  const eventPayload = {
    ...payload,
    funnel_session_id: getSessionId(),
    timestamp_ms: nowMs(),
  };

  const dataLayer = (window as any).dataLayer;
  if (Array.isArray(dataLayer)) {
    dataLayer.push(eventPayload);
  }

  window.dispatchEvent(new CustomEvent("dank:analytics", { detail: eventPayload }));

  if (process.env.NODE_ENV === "development") {
    console.info("[analytics:funnel]", eventPayload);
  }
}

export function trackFunnelEventOnce(
  dedupeKey: string,
  payload: FunnelEventPayload,
  ttlMs = 2000
): void {
  if (!dedupeKey) {
    trackFunnelEvent(payload);
    return;
  }
  if (isDuplicateKey(dedupeKey, ttlMs)) return;
  trackFunnelEvent(payload);
}
